/**
 * TrustGrid Control Results - Show ALL control evaluations (pass/fail/warning)
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Tag, Spin, Typography, Card, Modal, Descriptions } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, WarningOutlined, EyeOutlined } from '@ant-design/icons';
import { getLatestScan, getScanSignals } from '../api/trustgridApi';
import { useTrustGrid } from '../../../contexts/TrustGridContext';
import { showError } from '../../../utils/notifications';
import { handleCrossOrgError } from '../utils/errorHandlers';

const { Title, Text } = Typography;

function ControlResults() {
  const navigate = useNavigate();
  const { selectedProjectId, selectProject } = useTrustGrid();
  const projectId = selectedProjectId || 3;
  const [signals, setSignals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [scanId, setScanId] = useState(null);
  const [scanDate, setScanDate] = useState(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedSignal, setSelectedSignal] = useState(null);

  useEffect(() => {
    fetchLatestScanResults();
  }, [projectId]);

  const fetchLatestScanResults = async () => {
    setLoading(true);
    try {
      // Get latest scan
      const scanRes = await getLatestScan(projectId);
      const scan = scanRes.data;
      setScanId(scan.id);
      setScanDate(scan.started_at);

      // Get signals for that scan
      const signalsRes = await getScanSignals(scan.id);
      setSignals(signalsRes.data.signals || []);
    } catch (error) {
      const wasHandled = handleCrossOrgError(error, selectProject, navigate, 'ControlResults');
      if (!wasHandled) {
        console.error('Failed to fetch control results:', error);
        showError(error);
      }
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    if (status === 'pass') return <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 18 }} />;
    if (status === 'fail') return <CloseCircleOutlined style={{ color: '#f5222d', fontSize: 18 }} />;
    return <WarningOutlined style={{ color: '#faad14', fontSize: 18 }} />;
  };

  const getStatusTag = (status) => {
    const colors = {
      pass: 'success',
      fail: 'error',
      warning: 'warning'
    };
    return <Tag color={colors[status]}>{status.toUpperCase()}</Tag>;
  };

  const showDetailModal = (signal) => {
    setSelectedSignal(signal);
    setDetailModalVisible(true);
  };

  const columns = [
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status) => (
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {getStatusIcon(status)} {getStatusTag(status)}
        </span>
      ),
      filters: [
        { text: 'Pass', value: 'pass' },
        { text: 'Fail', value: 'fail' },
        { text: 'Warning', value: 'warning' }
      ],
      onFilter: (value, record) => record.status === value,
      sorter: (a, b) => {
        const order = { fail: 0, warning: 1, pass: 2 };
        return order[a.status] - order[b.status];
      },
      defaultSortOrder: 'ascend'
    },
    {
      title: 'Control',
      dataIndex: 'control_code',
      key: 'control_code',
      width: 120,
      render: (code) => <Text strong>{code}</Text>
    },
    {
      title: 'Title',
      dataIndex: 'control_title',
      key: 'control_title',
      ellipsis: true
    },
    {
      title: 'Domain',
      dataIndex: 'control_domain',
      key: 'control_domain',
      width: 150
    },
    {
      title: 'Confidence',
      dataIndex: 'confidence',
      key: 'confidence',
      width: 100,
      render: (conf) => `${conf?.toFixed(1) || 0}%`,
      sorter: (a, b) => (a.confidence || 0) - (b.confidence || 0)
    },
    {
      title: 'Evidence',
      dataIndex: 'evidence_count',
      key: 'evidence_count',
      width: 100,
      sorter: (a, b) => a.evidence_count - b.evidence_count
    },
    {
      title: 'Rationale',
      dataIndex: 'rationale',
      key: 'rationale',
      ellipsis: true,
      render: (text) => {
        if (!text) return <Text type="secondary">N/A</Text>;
        const preview = text.substring(0, 80);
        return preview + (text.length > 80 ? '...' : '');
      }
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: (_, record) => (
        <a onClick={() => showDetailModal(record)}>
          <EyeOutlined /> Details
        </a>
      )
    }
  ];

  const summary = {
    pass: signals.filter(s => s.status === 'pass').length,
    fail: signals.filter(s => s.status === 'fail').length,
    warning: signals.filter(s => s.status === 'warning').length
  };

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 24 }}>
        <Title level={3}>Control Evaluation Results</Title>
        <Text type="secondary">
          Complete scan results showing pass/fail/warning status for ALL controls
        </Text>
      </div>

      {scanId && (
        <Card size="small" style={{ marginBottom: 16, background: '#fafafa' }}>
          <div style={{ display: 'flex', gap: 32, alignItems: 'center' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 18 }} />
              <Text strong>Pass:</Text> {summary.pass}
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <CloseCircleOutlined style={{ color: '#f5222d', fontSize: 18 }} />
              <Text strong>Fail:</Text> {summary.fail}
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <WarningOutlined style={{ color: '#faad14', fontSize: 18 }} />
              <Text strong>Warning:</Text> {summary.warning}
            </span>
            <span style={{ marginLeft: 'auto' }}>
              <Text type="secondary">Scan #{scanId}</Text>
              {scanDate && (
                <Text type="secondary" style={{ marginLeft: 16 }}>
                  {new Date(scanDate).toLocaleString()}
                </Text>
              )}
            </span>
          </div>
        </Card>
      )}

      <Table
        columns={columns}
        dataSource={signals}
        rowKey="id"
        loading={loading}
        pagination={{ pageSize: 20, showSizeChanger: true, showTotal: (total) => `Total ${total} controls` }}
        size="middle"
      />

      <Modal
        title={`Control Details: ${selectedSignal?.control_code}`}
        visible={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={null}
        width={800}
      >
        {selectedSignal && (
          <Descriptions bordered column={1} size="small">
            <Descriptions.Item label="Control Code">
              {selectedSignal.control_code}
            </Descriptions.Item>
            <Descriptions.Item label="Title">
              {selectedSignal.control_title}
            </Descriptions.Item>
            <Descriptions.Item label="Domain">
              {selectedSignal.control_domain}
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {getStatusIcon(selectedSignal.status)} {getStatusTag(selectedSignal.status)}
              </span>
            </Descriptions.Item>
            <Descriptions.Item label="Confidence">
              {selectedSignal.confidence?.toFixed(1) || 0}%
            </Descriptions.Item>
            <Descriptions.Item label="Evidence Count">
              {selectedSignal.evidence_count}
            </Descriptions.Item>
            <Descriptions.Item label="Rationale">
              <div style={{ whiteSpace: 'pre-wrap', maxHeight: 300, overflow: 'auto' }}>
                {selectedSignal.rationale || 'N/A'}
              </div>
            </Descriptions.Item>
            {selectedSignal.evidence_refs && selectedSignal.evidence_refs.length > 0 && (
              <Descriptions.Item label="Evidence References">
                <div style={{ maxHeight: 200, overflow: 'auto' }}>
                  {selectedSignal.evidence_refs.slice(0, 10).map((ref, idx) => (
                    <div key={idx}>Event #{ref}</div>
                  ))}
                  {selectedSignal.evidence_refs.length > 10 && (
                    <Text type="secondary">
                      ... and {selectedSignal.evidence_refs.length - 10} more
                    </Text>
                  )}
                </div>
              </Descriptions.Item>
            )}
          </Descriptions>
        )}
      </Modal>
    </div>
  );
}

export default ControlResults;
